// @ts-nocheck
/**
 * "Get Notified" — public opt-in for event updates.
 *
 * The team picker is fed the `teams` array the published schedule already
 * returned, so this adds no new public read of anything. Signup itself goes
 * through the service-role `rle-subscribe-notifications` function.
 */
import React, { useState } from 'react';
import { Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { invokeEdgeFunction } from '@/lib/invokeEdgeFunction';

export const CONSENT_TEXT =
  'I agree to receive event updates about this tournament by email and/or text. ' +
  'Message and data rates may apply. Every message includes a way to stop.';

const WHOLE_EVENT = 'whole-event';
const OTHER_DIVISION = 'Other';

/** Sort division names the way the Division filter does: numeric first (12u, 13u, 14u), then alphabetical. */
const compareDivisions = (a: string, b: string) => {
  const na = parseFloat(a);
  const nb = parseFloat(b);
  const aNum = Number.isFinite(na);
  const bNum = Number.isFinite(nb);
  if (aNum && bNum && na !== nb) return na - nb;
  if (aNum !== bNum) return aNum ? -1 : 1;
  return a.localeCompare(b);
};

export const GetNotifiedButton: React.FC<{
  slug: string;
  teams?: { id: string; name: string; division?: string | null }[];
}> = ({ slug, teams = [] }) => {
  const [open, setOpen] = useState(false);
  const [registration, setRegistration] = useState(WHOLE_EVENT);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [wantsEmail, setWantsEmail] = useState(true);
  const [wantsSms, setWantsSms] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  // Group teams by division for the picker: divisions sorted like the
  // Division filter, teams by name within each group, null division last.
  const divisionGroups = React.useMemo(() => {
    const byDivision = new Map<string, { id: string; name: string }[]>();
    for (const t of teams) {
      const key = t.division || OTHER_DIVISION;
      if (!byDivision.has(key)) byDivision.set(key, []);
      byDivision.get(key)!.push(t);
    }
    const keys = [...byDivision.keys()].sort((a, b) => {
      if (a === OTHER_DIVISION) return 1;
      if (b === OTHER_DIVISION) return -1;
      return compareDivisions(a, b);
    });
    return keys.map((division) => ({
      division,
      teams: byDivision.get(division)!.slice().sort((a, b) => a.name.localeCompare(b.name)),
    }));
  }, [teams]);

  const reset = () => {
    setRegistration(WHOLE_EVENT);
    setName('');
    setEmail('');
    setPhone('');
    setWantsEmail(true);
    setWantsSms(false);
    setMessage(null);
    setDone(false);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setMessage(null);

    if (!email.trim() && !phone.trim()) {
      setMessage('Enter an email address or a mobile number.');
      return;
    }
    if (!wantsEmail && !wantsSms) {
      setMessage('Choose email updates, text updates, or both.');
      return;
    }
    if (wantsSms && !phone.trim()) {
      setMessage('Add a mobile number to get text updates.');
      return;
    }
    if (wantsEmail && !email.trim()) {
      setMessage('Add an email address to get email updates.');
      return;
    }

    setBusy(true);
    try {
      const { data, error } = await invokeEdgeFunction('rle-subscribe-notifications', {
        body: {
          slug,
          registration_id: registration === WHOLE_EVENT ? null : registration,
          name: name.trim() || null,
          email: email.trim() || null,
          phone: phone.trim() || null,
          wants_email: wantsEmail,
          wants_sms: wantsSms,
          consent_text: CONSENT_TEXT,
        },
      });
      if (error || !data?.ok) {
        setMessage(error?.message || "That didn't go through. Please try again.");
        return;
      }
      setDone(true);
    } catch {
      setMessage("That didn't go through. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <Button
        variant="outline"
        size="sm"
        onClick={() => {
          reset();
          setOpen(true);
        }}
      >
        <Bell className="mr-2 h-4 w-4" />
        Get Notified
      </Button>

      <Dialog
        open={open}
        onOpenChange={(v) => {
          setOpen(v);
          if (!v) reset();
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Get event updates</DialogTitle>
            <DialogDescription>
              We'll let you know about this event. Follow the whole event or just one team.
            </DialogDescription>
          </DialogHeader>

          {done ? (
            <div className="py-4">
              <p className="text-sm text-foreground">
                You're on the list. Every message we send includes a way to stop.
              </p>
              <div className="mt-4 flex justify-end">
                <Button onClick={() => setOpen(false)}>Close</Button>
              </div>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-1.5">
                <Label>Team</Label>
                <Select value={registration} onValueChange={setRegistration}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={WHOLE_EVENT}>Whole event</SelectItem>
                    {divisionGroups.map((group) => (
                      <SelectGroup key={group.division}>
                        <SelectLabel>{group.division}</SelectLabel>
                        {group.teams.map((t) => (
                          <SelectItem key={t.id} value={t.id}>
                            {t.name}
                          </SelectItem>
                        ))}
                      </SelectGroup>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="gn-name">Name (optional)</Label>
                <Input
                  id="gn-name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  autoComplete="name"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="gn-email">Email</Label>
                <Input
                  id="gn-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  autoComplete="email"
                  placeholder="you@example.com"
                />
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="gn-phone">Mobile (optional)</Label>
                <Input
                  id="gn-phone"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  autoComplete="tel"
                  placeholder="(555) 123-4567"
                />
              </div>

              <div className="space-y-2">
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={wantsEmail}
                    onChange={(e) => setWantsEmail(e.target.checked)}
                  />
                  Email me updates
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <input
                    type="checkbox"
                    checked={wantsSms}
                    onChange={(e) => setWantsSms(e.target.checked)}
                  />
                  Text me updates
                </label>
              </div>

              <p className="text-xs text-muted-foreground">{CONSENT_TEXT}</p>

              {message && <p className="text-sm text-destructive">{message}</p>}

              <div className="flex justify-end gap-2 pt-2">
                <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={busy}>
                  {busy ? 'Signing up…' : 'Sign me up'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GetNotifiedButton;
